import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiUnlink } from 'react-icons/bi';
import {
  FiBell,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiCopy,
  FiDownload,
  FiEdit,
  FiEye,
  FiLoader,
  FiMaximize2,
  FiMenu,
  FiMinimize2,
  FiMoreHorizontal,
  FiMoreVertical,
  FiPlusCircle,
  FiRefreshCw,
  FiRotateCcw,
  FiThumbsDown,
  FiThumbsUp,
  FiTrash2,
  FiXCircle,
} from 'react-icons/fi';
import { TbGripVertical } from 'react-icons/tb';

import type { ActionIconProps, ModalProps } from '@highlight-cp/core-ui';
import {
  ActionIcon,
  Alert,
  Flex,
  Text,
  Tooltip,
  useConfirmDeleteModal,
} from '@highlight-cp/core-ui';

import classes from './action-icons.module.css';

interface IconBaseProps extends React.SVGAttributes<SVGElement> {
  children?: React.ReactNode;
  size?: string | number;
  color?: string;
  title?: string;
}

type IconType = (props: IconBaseProps) => React.ReactNode;

export interface IconProps {
  ariaLabel?: string;
  /** Disclosure state, for a button that shows and hides a region. `ExpandCollapse` sets its own. */
  ariaExpanded?: boolean;
  /** The `id` of the region a disclosure button shows and hides. */
  ariaControls?: string;
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  circular?: boolean;
  dataTestId?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  size?: number;
  tooltip?: string;
  tooltipPosition?: 'bottom' | 'top' | 'left' | 'right';
  /**
   * Use these for selected/emphasised/muted states instead of a class — the underlying core-ui
   * ActionIcon locks `className`. If a call site needs positioning or a parent-hover reveal, wrap
   * the icon in a `Box` there rather than reaching through this component.
   */
  variant?: ActionIconProps['variant'];
  /** Overrides the default primary glyph colour. Semantic tokens only, e.g. `"dimmed"`. */
  c?: ActionIconProps['c'];
}

interface ActionIconsProps extends IconProps {
  Icon: IconType;
  ariaLabel: string;
}

interface DragIconProps extends Omit<IconProps, 'onClick'> {
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

interface DeleteProps extends IconProps {
  confirmationText?: string;
  confirmationTitle?: string;
  confirmationConfirmText?: string;
  confirmationAdditionalWarning?: string;
  confirmationVariant?: ModalProps['variant'];
  displayConfirmationModal?: boolean;
}

interface CopyProps extends IconProps {
  confirmationText?: string;
  confirmationTitle?: string;
  confirmationConfirmText?: string;
  displayConfirmationModal?: boolean;
}

const ActionIcons = ({
  Icon,
  buttonSize,
  circular,
  dataTestId,
  tooltip,
  tooltipPosition = 'top',
  ariaControls,
  ariaExpanded,
  ariaLabel,
  size = 20,
  isLoading,
  onClick,
  c,
  ...props
}: ActionIconsProps) => {
  const { t } = useTranslation();
  const [isTooltipOpened, setIsTooltipOpened] = useState(false);
  const handleOpenTooltip = useCallback(() => setIsTooltipOpened(true), []);
  const handleCloseTooltip = useCallback(() => setIsTooltipOpened(false), []);
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsTooltipOpened(false);
      onClick?.(e);
    },
    [onClick],
  );

  // `classes.icon`'s hover matched Mantine's `subtle` (core-ui ActionIcon's default), but its
  // colour did not: the rule was unlayered, so it beat `subtle`'s layered `--ai-color` and every
  // glyph rendered `primary-color-filled`, not the darker `primary-color-light-color`. Keep it via
  // `c`, except while disabled — an inline colour would beat Mantine's disabled rule too and leave
  // a bright glyph on a greyed-out button. `circular` becomes a radius.
  const actionIcon = (
    <ActionIcon
      size={buttonSize}
      radius={circular ? '50%' : undefined}
      c={props.disabled ? undefined : (c ?? 'var(--mantine-primary-color-filled)')}
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      aria-label={t(ariaLabel)}
      data-testid={dataTestId}
      {...props}
      // Control the tooltip opening and closing to prevent issues with tooltips persisting
      // when the icon opens something like a modal or overlay
      onMouseEnter={tooltip ? handleOpenTooltip : undefined}
      onMouseLeave={tooltip ? handleCloseTooltip : undefined}
      onFocus={tooltip ? handleOpenTooltip : undefined}
      onBlur={tooltip ? handleCloseTooltip : undefined}
      onClick={handleClick}
    >
      {isLoading ? <FiLoader className={classes.loader} size={size} /> : <Icon size={size} />}
    </ActionIcon>
  );

  // Consumers use `className` to position the button or drive a parent-hover reveal, neither of
  // which has a prop equivalent. It goes on a Box (an atomic surface, where `className` stays
  // open) — and only when one is passed, so the common case keeps its DOM shape.
  if (tooltip) {
    return (
      <Tooltip label={t(tooltip)} opened={isTooltipOpened} position={tooltipPosition}>
        {props.disabled ? (
          <div
            onMouseEnter={handleOpenTooltip}
            onMouseLeave={handleCloseTooltip}
            onFocus={handleOpenTooltip}
            onBlur={handleCloseTooltip}
            style={{ display: 'inline-block' }}
          >
            {actionIcon}
          </div>
        ) : (
          actionIcon
        )}
      </Tooltip>
    );
  }

  return actionIcon;
};

const Edit = (props: IconProps) => {
  return <ActionIcons Icon={FiEdit} ariaLabel={props.ariaLabel || 'Edit'} {...props} />;
};

const Copy = (props: CopyProps) => {
  const {
    ariaLabel,
    onClick,
    confirmationText,
    confirmationTitle,
    confirmationConfirmText,
    displayConfirmationModal,
    ...rest
  } = props;
  const { t } = useTranslation();
  const { confirmDelete } = useConfirmDeleteModal({
    children: <Text>{t(confirmationText || 'Are you sure you want to copy?')}</Text>,
    confirmText: t(confirmationConfirmText || 'Copy'),
    title: t(confirmationTitle || 'Copy'),
  });
  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (displayConfirmationModal) {
      confirmDelete({
        onConfirm: () => {
          onClick?.(e);
        },
      });
    } else {
      onClick?.(e);
    }
  };

  return (
    <ActionIcons Icon={FiCopy} ariaLabel={ariaLabel || 'Copy'} onClick={handleCopy} {...rest} />
  );
};

const Confirm = (props: IconProps) => {
  return <ActionIcons Icon={FiCheckCircle} ariaLabel={props.ariaLabel || 'Confirm'} {...props} />;
};

const Cancel = (props: IconProps) => {
  return <ActionIcons Icon={FiXCircle} ariaLabel={props.ariaLabel || 'Cancel'} {...props} />;
};

const Add = (props: IconProps) => {
  return <ActionIcons Icon={FiPlusCircle} ariaLabel={props.ariaLabel || 'Add'} {...props} />;
};

const Drag = (props: DragIconProps) => {
  return (
    <ActionIcons
      Icon={TbGripVertical}
      ariaLabel={props.ariaLabel || 'Drag'}
      // onClick is optional for the drag icon (probably only used for tracking purposes or whatever)
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick={props.onClick || (() => {})}
      {...props}
    />
  );
};

const Preview = (props: IconProps) => {
  return <ActionIcons Icon={FiEye} ariaLabel={props.ariaLabel || 'Preview'} {...props} />;
};

const Reset = (props: IconProps) => {
  return <ActionIcons Icon={FiRotateCcw} ariaLabel={props.ariaLabel || 'Reset'} {...props} />;
};

/**
 * Expand/collapse toggle — swaps the chevron glyph on one button.
 *
 * Every consumer rendered `isExpanded ? <Collapse /> : <Expand />`, which mounts a *different*
 * component type in the same position, so React unmounted the node the user had just activated and
 * focus fell back to `<body>`. Swapping only the icon keeps the button mounted, and keyboard focus
 * with it.
 *
 * The state reaches assistive tech as `aria-expanded` rather than as a changing label alone, which
 * only tells a screen-reader user what the button does next. Pass `ariaControls` where the region
 * it toggles has an `id`, to complete the disclosure pattern.
 */
const ExpandCollapse = ({
  isExpanded,
  ariaLabel,
  ...props
}: IconProps & { isExpanded: boolean }) => {
  return (
    <ActionIcons
      {...props}
      Icon={isExpanded ? FiChevronUp : FiChevronDown}
      ariaExpanded={isExpanded}
      ariaLabel={ariaLabel || (isExpanded ? 'Collapse' : 'Expand')}
    />
  );
};

const Delete = (props: DeleteProps) => {
  const {
    ariaLabel,
    onClick,
    confirmationText,
    confirmationTitle,
    confirmationConfirmText,
    confirmationAdditionalWarning,
    confirmationVariant,
    displayConfirmationModal = true,
    ...rest
  } = props;
  const { t } = useTranslation();
  const { confirmDelete } = useConfirmDeleteModal({
    children: (
      <>
        <Text>{t(confirmationText || 'Are you sure you want to delete?')}</Text>
        {confirmationAdditionalWarning && (
          <Alert color="red" mt="sm">
            {t(`Warning: ${confirmationAdditionalWarning}`)}
          </Alert>
        )}
      </>
    ),
    confirmText: t(confirmationConfirmText || 'Delete'),
    title: t(confirmationTitle || 'Delete'),
    variant: confirmationVariant,
  });
  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (displayConfirmationModal) {
      confirmDelete({
        onConfirm: () => {
          onClick?.(e);
        },
      });
    } else {
      onClick?.(e);
    }
  };

  return (
    <ActionIcons
      Icon={FiTrash2}
      ariaLabel={ariaLabel || 'Delete'}
      onClick={handleDelete}
      {...rest}
    />
  );
};

const MenuHorizontal = (props: IconProps) => {
  return <ActionIcons Icon={FiMoreHorizontal} ariaLabel={props.ariaLabel || 'Menu'} {...props} />;
};

const MenuVertical = (props: IconProps) => {
  return <ActionIcons Icon={FiMoreVertical} ariaLabel={props.ariaLabel || 'Menu'} {...props} />;
};

const Menu = (props: IconProps) => {
  return <ActionIcons Icon={FiMenu} ariaLabel={props.ariaLabel || 'Menu'} {...props} />;
};

const Maximize = (props: IconProps) => {
  return <ActionIcons Icon={FiMaximize2} ariaLabel={props.ariaLabel || 'Maximize'} {...props} />;
};

const Minimize = (props: IconProps) => {
  return <ActionIcons Icon={FiMinimize2} ariaLabel={props.ariaLabel || 'Minimize'} {...props} />;
};

const TryAgain = (props: IconProps) => {
  return <ActionIcons Icon={FiRefreshCw} ariaLabel={props.ariaLabel || 'Try again'} {...props} />;
};

const ActionIconsFlex = ({ children }: { children: React.ReactNode }) => {
  return <Flex gap={8}>{children}</Flex>;
};

const Download = (props: IconProps) => {
  return <ActionIcons Icon={FiDownload} ariaLabel={props.ariaLabel || 'Download'} {...props} />;
};

const ThumbsDown = (props: IconProps) => {
  return (
    <ActionIcons Icon={FiThumbsDown} ariaLabel={props.ariaLabel || 'Thumbs Down'} {...props} />
  );
};

const ThumbsUp = (props: IconProps) => {
  return <ActionIcons Icon={FiThumbsUp} ariaLabel={props.ariaLabel || 'Thumbs Up'} {...props} />;
};

const Unlink = (props: IconProps) => {
  return <ActionIcons Icon={BiUnlink} ariaLabel={props.ariaLabel || 'Unlink'} {...props} />;
};

const Notifications = (props: IconProps) => {
  return <ActionIcons Icon={FiBell} ariaLabel={props.ariaLabel || 'Notifications'} {...props} />;
};

/**
 * ActionIcons
 * ---
 * @description
 * ActionIcons is a component that provides a set of action icons for the application.
 * It is used to provide a consistent look and feel for the action icons.
 * @example
 * ```tsx
 * <ActionIcons.Flex>
 *   <ActionIcons.Edit />
 *   <ActionIcons.Delete />
 * </ActionIcons.Flex>
 * ```
 * @exports
 * @default
 * @see {@link ActionIcons.Flex}
 * @see {@link ActionIcons.Edit}
 * @see {@link ActionIcons.Delete}
 * @see {@link ActionIcons.Copy}
 * @see {@link ActionIcons.Confirm}
 * @see {@link ActionIcons.Cancel}
 * @see {@link ActionIcons.Add}
 * @see {@link ActionIcons.Download}
 */
export default {
  Add,
  Cancel,
  Confirm,
  Copy,
  Delete,
  Download,
  Drag,
  Edit,
  ExpandCollapse,
  Flex: ActionIconsFlex,
  Maximize,
  Menu,
  MenuHorizontal,
  MenuVertical,
  Minimize,
  Notifications,
  Preview,
  Reset,
  ThumbsDown,
  ThumbsUp,
  TryAgain,
  Unlink,
};
